import { useEffect, useState } from "react";
import { HardDriveDownload, Cloud, CloudOff, RefreshCw, Download, FolderOpen, Loader2, ChevronRight, ChevronsUpDown, Copy, Check, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDateTime } from "@/lib/time";

const KB = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round((b || 0) / 1024)} KB`);

export default function BackupSettings() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [folderOpen, setFolderOpen] = useState(false);
  const [browsePath, setBrowsePath] = useState([{ id: "root", name: "Il mio Drive" }]);
  const [children, setChildren] = useState([]);
  const [browsing, setBrowsing] = useState(false);
  const [folderQ, setFolderQ] = useState("");
  const [running, setRunning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const doRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/backup/restore/${restoreTarget.id}`, null, { params: { fmt: "bson" } });
      toast.success(`Ripristino completato: ${data.documents} documenti in ${data.collections} collezioni`);
      setRestoreTarget(null);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestoring(false); }
  };

  const copyRedirect = () => {
    if (!status?.redirect_uri) return;
    navigator.clipboard.writeText(status.redirect_uri);
    setCopied(true);
    toast.success("Redirect URI copiato");
    setTimeout(() => setCopied(false), 2000);
  };

  const loadChildren = async (parentId) => {
    setBrowsing(true);
    try { const r = await api.get("/drive/folders", { params: { parent: parentId } }); setChildren(r.data || []); }
    catch (e) { toast.error("Errore cartelle Drive: " + apiError(e)); }
    finally { setBrowsing(false); }
  };
  const openPicker = (open) => { setFolderOpen(open); if (open) { setFolderQ(""); loadChildren(browsePath[browsePath.length - 1].id); } };
  const enterFolder = (f) => { const p = [...browsePath, f]; setBrowsePath(p); setFolderQ(""); loadChildren(f.id); };
  const goCrumb = (idx) => { const p = browsePath.slice(0, idx + 1); setBrowsePath(p); setFolderQ(""); loadChildren(p[p.length - 1].id); };
  const pickFolder = (f) => { saveSettings({ drive_folder_id: f.id, drive_folder_name: f.name }); setFolderOpen(false); toast.success(`Cartella backup: ${f.name}`); };
  const pickCurrent = () => { const cur = browsePath[browsePath.length - 1]; if (cur.id === "root") { toast.error("Entra in una cartella specifica"); return; } pickFolder(cur); };

  const loadAll = async () => {
    try {
      const [st, se, hi] = await Promise.all([
        api.get("/drive/status"),
        api.get("/backup/settings"),
        api.get("/backup/history"),
      ]);
      setStatus(st.data); setSettings(se.data); setHistory(hi.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    loadAll();
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive") === "connected") { toast.success("Google Drive collegato"); window.history.replaceState({}, "", "/impostazioni"); }
    if (params.get("drive") === "error") { toast.error("Collegamento Google Drive fallito"); window.history.replaceState({}, "", "/impostazioni"); }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try { const { data } = await api.get("/drive/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(apiError(e)); setConnecting(false); }
  };
  const disconnect = async () => {
    try { await api.post("/drive/disconnect"); toast.success("Google Drive scollegato"); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveSettings = async (patch) => {
    try { const { data } = await api.put("/backup/settings", patch); setSettings((s) => ({ ...s, ...data })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const { data: job } = await api.post("/backup/run");
      toast.info("Backup avviato: puoi continuare a lavorare, ti avviso al termine");
      loadAll();
      for (;;) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data } = await api.get(`/backup/status/${job.id}`);
        if (data.status === "running") continue;
        if (data.status === "failed") toast.error(`Backup non riuscito: ${data.error || "errore sconosciuto"}`);
        else toast.success(data.drive?.uploaded ? "Backup creato e caricato su Drive" : "Backup creato (locale)");
        break;
      }
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };
  const download = async (bid, fmt) => {
    try {
      const res = await api.get(`/backup/download/${bid}/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${bid}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="backup-section">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><HardDriveDownload size={18} className="text-primary" /><h3 className="font-bold text-navy">Backup Database</h3></div>
        <Button onClick={runNow} disabled={running} data-testid="backup-run-btn">
          {running ? <Loader2 size={16} className="animate-spin" /> : <HardDriveDownload size={16} />}Backup ora
        </Button>
      </div>

      {/* Google Drive */}
      <div className="mb-4 rounded-lg border border-border bg-secondary/20 p-4">
        {status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm"><Cloud size={16} className="text-emerald-600" /><span className="font-semibold text-navy">Google Drive collegato</span><span className="text-muted-foreground">{status.email}</span></div>
              <Button variant="outline" size="sm" onClick={disconnect} data-testid="drive-disconnect-btn"><CloudOff size={14} />Scollega</Button>
            </div>
            <div className="flex items-center gap-2">
              <FolderOpen size={16} className="shrink-0 text-muted-foreground" />
              <Popover open={folderOpen} onOpenChange={openPicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full max-w-md justify-between font-normal" data-testid="drive-folder-select">
                    <span className="truncate">{settings?.drive_folder_name || "Seleziona cartella (e sottocartella) Drive"}</span>
                    <ChevronsUpDown size={16} className="ml-2 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <div className="border-b border-border p-2">
                    <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                      {browsePath.map((p, i) => (
                        <span key={p.id} className="flex items-center gap-1">
                          {i > 0 && <ChevronRight size={11} />}
                          <button onClick={() => goCrumb(i)} className="max-w-[120px] truncate hover:text-navy" data-testid={`drive-crumb-${i}`}>{p.name}</button>
                        </span>
                      ))}
                    </div>
                    <Input className="mt-2 h-8" placeholder="Cerca in questa cartella…" value={folderQ} onChange={(e) => setFolderQ(e.target.value)} data-testid="drive-folder-search" />
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {browsing ? (
                      <p className="p-3 text-center text-sm text-muted-foreground"><Loader2 size={14} className="mr-1 inline animate-spin" />Caricamento…</p>
                    ) : (() => {
                      const list = children.filter((f) => f.name.toLowerCase().includes(folderQ.toLowerCase()));
                      if (!list.length) return <p className="p-3 text-center text-sm text-muted-foreground">Nessuna sottocartella</p>;
                      return list.map((f) => (
                        <div key={f.id} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-secondary" data-testid={`drive-folder-opt-${f.id}`}>
                          <button onClick={() => enterFolder(f)} className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-navy">
                            <FolderOpen size={14} className="shrink-0 text-primary" /><span className="truncate">{f.name}</span>
                          </button>
                          <Button size="sm" variant="ghost" className="h-7 shrink-0 text-primary" onClick={() => pickFolder(f)} data-testid={`drive-folder-choose-${f.id}`}>Scegli</Button>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-border p-2">
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">Qui: {browsePath[browsePath.length - 1].name}</span>
                    <Button size="sm" onClick={pickCurrent} data-testid="drive-folder-choose-current" disabled={browsePath.length <= 1}>Usa questa cartella</Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm"><CloudOff size={16} className="text-muted-foreground" /><span className="text-muted-foreground">Google Drive non collegato — i backup restano solo in locale</span></div>
            <Button variant="outline" size="sm" onClick={connect} disabled={connecting} data-testid="drive-connect-btn"><Cloud size={14} />Collega Google Drive</Button>
          </div>
        )}
      </div>

      {/* Redirect URI per Google Console */}
      {status?.redirect_uri && (
        <div className="mb-4 rounded-lg border border-dashed border-border bg-secondary/10 p-3" data-testid="drive-redirect-uri-box">
          <p className="mb-1.5 text-xs font-semibold text-navy">URI di reindirizzamento autorizzato (per Google Cloud Console)</p>
          <p className="mb-2 text-xs text-muted-foreground">Copia e incolla questo valore in <span className="font-medium">Credenziali → ID client OAuth → URI di reindirizzamento autorizzati</span>.</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-2 py-1.5 text-xs text-navy" data-testid="drive-redirect-uri-value">{status.redirect_uri}</code>
            <Button variant="outline" size="sm" onClick={copyRedirect} className="shrink-0" data-testid="drive-redirect-uri-copy">
              {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              {copied ? "Copiato" : "Copia"}
            </Button>
          </div>
        </div>
      )}

      {/* Auto backup */}
      <div className="mb-4 flex items-center justify-between border-b border-border/60 pb-4">
        <div>
          <p className="font-semibold text-navy">Backup automatico</p>
          <p className="text-sm text-muted-foreground">Esecuzione giornaliera alle 02:00 (fusomorario Europe/Rome). Formati: JSON e BSON (eseguito in un processo separato per non appesantire il server).</p>
        </div>
        <Switch checked={!!settings?.auto_enabled} onCheckedChange={(v) => saveSettings({ auto_enabled: v })} data-testid="backup-auto-toggle" />
      </div>

      {/* History */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-bold text-navy">Ultimi backup</p>
          <button onClick={loadAll} className="text-muted-foreground hover:text-navy" data-testid="backup-refresh"><RefreshCw size={14} /></button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun backup ancora eseguito.</p>
        ) : (
          <div className="space-y-2">
            {history.map((b, bi) => (
              <div key={`${b.id}-${bi}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`backup-row-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy">{fmtDateTime(b.created_at)} · <span className="font-normal capitalize text-muted-foreground">{b.trigger}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {b.status === "running" ? <span className="font-semibold text-primary" data-testid={`backup-running-${b.id}`}>Backup in corso…</span>
                      : b.status === "failed" ? <span className="font-semibold text-destructive">Non riuscito{b.error ? `: ${b.error}` : ""}</span>
                      : <>{b.documents} documenti · {b.collections} collezioni {b.drive?.uploaded ? "· ☁ su Drive" : ""}{b.local === false ? " · solo su Drive" : ""}</>}
                  </p>
                </div>
                {(!b.status || b.status === "ok") && (
                <div className="flex shrink-0 items-center gap-1">
                  {b.local !== false && Object.keys(b.files || { json: 1, bson: 1 }).map((fmt) => (
                    <Button key={fmt} variant="outline" size="sm" onClick={() => download(b.id, fmt)} data-testid={`backup-dl-${b.id}-${fmt}`}>
                      <Download size={13} />{fmt}
                    </Button>
                  ))}
                  {b.local !== false && <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setRestoreTarget(b)} data-testid={`backup-restore-${b.id}`}>
                    <RotateCcw size={13} />Ripristina
                  </Button>}
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && !restoring && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" />Ripristinare questo backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno <b>sovrascritti tutti i dati attuali</b> (ticket, anagrafiche, utenti, listini…) con quelli del backup del <b>{restoreTarget ? fmtDateTime(restoreTarget.created_at) : ""}</b>.
              Le impostazioni di backup e il collegamento a Google Drive vengono mantenuti. L'operazione non è reversibile: assicurati di aver eseguito un backup recente dello stato corrente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} data-testid="restore-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doRestore(); }} disabled={restoring} className="bg-amber-600 hover:bg-amber-700" data-testid="restore-confirm">
              {restoring ? <><Loader2 size={14} className="animate-spin" />Ripristino…</> : <>Ripristina ora</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
